/**
 * Pose Library & Character Profile save/load utilities.
 *
 * Poses and character profiles are global library items (not scene-specific).
 * They store mannequin bone quaternions (poses) or morph weights + proportions
 * (characters) as JSON files in the cloud library alongside scenes and objects.
 */

import type { LibraryItemMetadata, LibraryItemType } from "@poseref/shared";
import { uploadToPresignedUrl } from "../api/client.ts";
import * as libraryApi from "../api/library.ts";
import { getPresignedUploadUrl } from "../api/upload.ts";
import { getRestPose } from "../mannequin/boneRegistry.ts";
import { type Quat4, qmul } from "../mannequin/quaternionMath.ts";
import { TWIST_BONE_NAMES } from "../mannequin/twistBones.ts";
import { clearLastAppliedPresetName } from "./defaultPoses.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { useStore } from "./store.ts";
import { captureViewportThumbnail } from "./thumbnailCapture.ts";

// ---------------------------------------------------------------------------
// File formats
// ---------------------------------------------------------------------------

export interface PoseFile {
  magic: "POSEREF_POSE_V1";
  /** Bone name → quaternion [x, y, z, w] */
  bonePoses: Record<string, [number, number, number, number]>;
  /** Number of bones in the pose */
  boneCount: number;
  /** Model ID the pose was created with (e.g. "male-neutral") */
  modelId?: string;
  /**
   * How to interpret the quaternions:
   * - "absolute" (default): set bone.quaternion directly
   * - "delta": parent-frame delta composed as q_delta * q_rest
   *
   * BVH-converted poses use "delta" since the conversion subtracts the BVH
   * rest pose but doesn't know the target mannequin's rest quaternions.
   * Hand-crafted and user-saved poses use "absolute".
   */
  poseSpace?: "absolute" | "delta";
}

export interface CharacterFile {
  magic: "POSEREF_CHARACTER_V1";
  /** Morph name → weight */
  morphs: Record<string, number>;
  /** Region ID → scale (bone volumes / proportions) */
  proportions: Record<string, number>;
  /** Model ID the character was created with */
  modelId?: string;
}

type Q4 = Quat4;

/**
 * Convert a PoseFile's bonePoses into an absolute-quaternion Map,
 * composing deltas with the mannequin rest pose when poseSpace is "delta".
 *
 * Delta poses store parent-frame deltas: q_delta = q_bvh_frame * inv(q_bvh_rest).
 * To apply:  q_posed = q_delta * q_mannequin_rest   (pre-multiply in parent frame).
 */
export function resolveBonePoses(
  data: PoseFile,
  objectId: string,
): Map<string, Q4> {
  const poses = new Map<string, Q4>();
  const isDelta = data.poseSpace === "delta";

  for (const [name, q] of Object.entries(data.bonePoses)) {
    if (isDelta) {
      const rest = getRestPose(objectId, name);
      // q_posed = q_delta * q_rest  (parent-frame composition)
      poses.set(name, rest ? qmul(q as Q4, rest) : (q as Q4));
    } else {
      poses.set(name, q as Q4);
    }
  }
  return poses;
}

// ---------------------------------------------------------------------------
// Capture current state
// ---------------------------------------------------------------------------

function capturePoseData(objectId: string): PoseFile | null {
  const state = useStore.getState();
  const bonePoses = state.mannequinBonePoses.get(objectId);
  if (!bonePoses || bonePoses.size === 0) return null;

  const poses: Record<string, [number, number, number, number]> = {};
  for (const [name, q] of bonePoses) {
    if (TWIST_BONE_NAMES.has(name)) continue;
    poses[name] = q;
  }

  // Try to find model ID
  const obj = state.sceneObjects.get(objectId);
  const modelId = obj?.mannequinModelId ?? undefined;

  return {
    magic: "POSEREF_POSE_V1",
    bonePoses: poses,
    boneCount: bonePoses.size,
    modelId,
  };
}

function captureCharacterData(objectId: string): CharacterFile | null {
  const state = useStore.getState();

  const morphs: Record<string, number> = {};
  const morphMap = state.mannequinMorphs.get(objectId);
  if (morphMap) {
    for (const [name, weight] of morphMap) {
      morphs[name] = weight;
    }
  }

  const proportions: Record<string, number> = {};
  const volumeMap = state.boneVolumes.get(objectId);
  if (volumeMap) {
    for (const [region, scale] of volumeMap) {
      proportions[region] = scale;
    }
  }

  // At least one non-default value required
  const hasData =
    Object.values(morphs).some((v) => v !== 0) ||
    Object.values(proportions).some((v) => v !== 1);
  if (!hasData) return null;

  const obj = state.sceneObjects.get(objectId);

  return {
    magic: "POSEREF_CHARACTER_V1",
    morphs,
    proportions,
    modelId: obj?.mannequinModelId ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Save to cloud library
// ---------------------------------------------------------------------------

export async function uploadLibraryItem(
  type: LibraryItemType,
  name: string,
  data: object,
  metadata?: LibraryItemMetadata,
): Promise<boolean> {
  try {
    const fileBlob = new Blob([JSON.stringify(data)], {
      type: "application/json",
    });

    const thumbnailBlob = await captureViewportThumbnail();

    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/${type}s/${timestamp}_${rand}.json`;
    const thumbnailKey = thumbnailBlob
      ? `library/thumbnails/${timestamp}_${rand}.webp`
      : undefined;

    const filePresign = await getPresignedUploadUrl(
      fileKey,
      "application/json",
    );
    await uploadToPresignedUrl(filePresign.url, fileBlob);

    if (thumbnailBlob && thumbnailKey) {
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/webp",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbnailBlob);
    }

    await useLibraryStore.getState().createItem({
      name,
      type,
      fileKey,
      thumbnailKey,
      metadata,
    });

    return true;
  } catch (e) {
    console.error(`Failed to save ${type} to library:`, e);
    return false;
  }
}

export async function savePoseToLibrary(
  objectId: string,
  name: string,
): Promise<boolean> {
  const data = capturePoseData(objectId);
  if (!data) return false;

  return uploadLibraryItem("pose", name, data, {
    objectCount: data.boneCount,
    sourceFormat: data.modelId,
  });
}

export async function saveCharacterToLibrary(
  objectId: string,
  name: string,
): Promise<boolean> {
  const data = captureCharacterData(objectId);
  if (!data) return false;

  const morphCount = Object.keys(data.morphs).length;
  const propCount = Object.keys(data.proportions).length;

  return uploadLibraryItem("character", name, data, {
    objectCount: morphCount + propCount,
    sourceFormat: data.modelId,
  });
}

// ---------------------------------------------------------------------------
// Load from cloud library
// ---------------------------------------------------------------------------

export async function loadPoseFromLibrary(
  fileUrl: string,
  objectId: string,
): Promise<boolean> {
  try {
    const res = await fetch(fileUrl);
    const data = (await res.json()) as PoseFile;
    if (data.magic !== "POSEREF_POSE_V1") return false;

    const poses = resolveBonePoses(data, objectId);

    // Reset first so bones not in this pose return to rest
    useStore.getState().resetPose(objectId);
    useStore.getState().setBonePoses(objectId, poses);
    clearLastAppliedPresetName();
    return true;
  } catch (e) {
    console.error("Failed to load pose:", e);
    return false;
  }
}

export async function loadCharacterFromLibrary(
  fileUrl: string,
  objectId: string,
): Promise<boolean> {
  try {
    const res = await fetch(fileUrl);
    const data = (await res.json()) as CharacterFile;
    if (data.magic !== "POSEREF_CHARACTER_V1") return false;

    const store = useStore.getState();

    // Swap model if the profile specifies a different variant
    if (data.modelId) {
      const currentModelId = store.sceneObjects.get(objectId)?.mannequinModelId;
      // Migrate old "male" model ID to "male-neutral"
      const targetModelId =
        data.modelId === "male" ? "male-neutral" : data.modelId;
      if (targetModelId !== currentModelId) {
        store.setMannequinModelId(objectId, targetModelId);
      }
    }

    // Apply proportions (skip body_type morphs from old profiles)
    for (const [name, weight] of Object.entries(data.morphs)) {
      if (name === "body_type") continue;
      store.setMannequinMorph(objectId, name, weight);
    }

    // Apply proportions
    for (const [region, scale] of Object.entries(data.proportions)) {
      store.setBoneVolume(objectId, region, scale);
    }

    return true;
  } catch (e) {
    console.error("Failed to load character:", e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Overwrite existing library item with current state
// ---------------------------------------------------------------------------

export async function uploadAndUpdateItem(
  itemId: string,
  type: LibraryItemType,
  data: object,
  metadata?: LibraryItemMetadata,
): Promise<boolean> {
  try {
    const fileBlob = new Blob([JSON.stringify(data)], {
      type: "application/json",
    });
    const thumbnailBlob = await captureViewportThumbnail();

    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/${type}s/${timestamp}_${rand}.json`;
    const thumbnailKey = thumbnailBlob
      ? `library/thumbnails/${timestamp}_${rand}.webp`
      : undefined;

    const filePresign = await getPresignedUploadUrl(
      fileKey,
      "application/json",
    );
    await uploadToPresignedUrl(filePresign.url, fileBlob);

    if (thumbnailBlob && thumbnailKey) {
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/webp",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbnailBlob);
    }

    await libraryApi.updateItem(itemId, { fileKey, thumbnailKey, metadata });
    return true;
  } catch (e) {
    console.error(`Failed to overwrite ${type}:`, e);
    return false;
  }
}

export async function overwritePoseInLibrary(
  itemId: string,
  objectId: string,
): Promise<boolean> {
  const data = capturePoseData(objectId);
  if (!data) return false;
  const ok = await uploadAndUpdateItem(itemId, "pose", data, {
    objectCount: data.boneCount,
    sourceFormat: data.modelId,
  });
  if (ok) await useLibraryStore.getState().fetchItems();
  return ok;
}

export async function overwriteCharacterInLibrary(
  itemId: string,
  objectId: string,
): Promise<boolean> {
  const data = captureCharacterData(objectId);
  if (!data) return false;
  const morphCount = Object.keys(data.morphs).length;
  const propCount = Object.keys(data.proportions).length;
  const ok = await uploadAndUpdateItem(itemId, "character", data, {
    objectCount: morphCount + propCount,
    sourceFormat: data.modelId,
  });
  if (ok) await useLibraryStore.getState().fetchItems();
  return ok;
}

// ---------------------------------------------------------------------------
// Duplicate an existing library item
// ---------------------------------------------------------------------------

export async function duplicateLibraryItem(itemId: string): Promise<boolean> {
  try {
    const store = useLibraryStore.getState();
    const item = store.items.find((i) => i.id === itemId);
    if (!item || !item.fileUrl) return false;

    // Download original file
    const res = await fetch(item.fileUrl);
    const blob = await res.blob();

    // Re-upload under a new key
    const timestamp = Date.now();
    const rand = Math.random().toString(36).slice(2, 8);
    const fileKey = `library/${item.type}s/${timestamp}_${rand}.json`;

    const presign = await getPresignedUploadUrl(fileKey, "application/json");
    await uploadToPresignedUrl(presign.url, blob);

    // Copy thumbnail if present
    let thumbnailKey: string | undefined;
    if (item.thumbnailUrl) {
      const thumbRes = await fetch(item.thumbnailUrl);
      const thumbBlob = await thumbRes.blob();
      thumbnailKey = `library/thumbnails/${timestamp}_${rand}.webp`;
      const thumbPresign = await getPresignedUploadUrl(
        thumbnailKey,
        "image/webp",
      );
      await uploadToPresignedUrl(thumbPresign.url, thumbBlob);
    }

    await store.createItem({
      name: `${item.name} (Copy)`,
      type: item.type,
      fileKey,
      thumbnailKey,
      folderId: item.folderId,
      metadata: item.metadata ?? undefined,
    });

    return true;
  } catch (e) {
    console.error("Failed to duplicate item:", e);
    return false;
  }
}
